// Sons in-memory (WAV) tocados via Audio:
//  - tocarBeep(): "blim-blaum" tipo campainha — mensagem inbound nova.
//  - tocarAlertaChamado(): 3 notas ascendentes "ploin-ploin-ploin" — chamado NOVO
//    atribuido pra atendente (mais urgente, distinto do beep de mensagem).

let chimeUri: string | null = null;
let alertaUri: string | null = null;
let unlocked = false;

// Empacota amostras Int16 mono em data-URI WAV.
function encodeWav(samples: Int16Array, sampleRate: number): string {
  const numSamples = samples.length;
  const dataSize = numSamples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  for (let i = 0; i < numSamples; i++) view.setInt16(44 + i * 2, samples[i]!, true);
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return 'data:audio/wav;base64,' + btoa(bin);
}

function gerarWav(): string {
  const sampleRate = 22050;
  const noteLen = 0.35;
  const gap = 0.08;
  const total = noteLen + gap + noteLen + 0.1;
  const numSamples = Math.floor(sampleRate * total);
  const blaumStart = noteLen + gap;
  const samples = new Int16Array(numSamples);
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    let val = 0;
    if (t < noteLen) {
      const env = Math.exp(-t * 6);
      val = (Math.sin(2 * Math.PI * 1318 * t) * 0.5 + Math.sin(2 * Math.PI * 2636 * t) * 0.2 + Math.sin(2 * Math.PI * 3954 * t) * 0.08) * env * 0.7;
    }
    if (t >= blaumStart && t < blaumStart + noteLen + 0.1) {
      const t2 = t - blaumStart;
      const env2 = Math.exp(-t2 * 5);
      val += (Math.sin(2 * Math.PI * 1047 * t2) * 0.5 + Math.sin(2 * Math.PI * 2094 * t2) * 0.2 + Math.sin(2 * Math.PI * 3141 * t2) * 0.08) * env2 * 0.7;
    }
    samples[i] = Math.max(-32768, Math.min(32767, Math.floor(val * 32767)));
  }
  return encodeWav(samples, sampleRate);
}

// 3 notas ascendentes (C5, E5, G5) — alerta de chamado novo, mais "pra cima".
function gerarAlertaWav(): string {
  const sampleRate = 22050;
  const notas = [523, 659, 784];
  const noteLen = 0.16;
  const total = notas.length * noteLen + 0.12;
  const numSamples = Math.floor(sampleRate * total);
  const samples = new Int16Array(numSamples);
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const idx = Math.floor(t / noteLen);
    const nota = notas[idx];
    let val = 0;
    if (nota !== undefined) {
      const t2 = t - idx * noteLen;
      const env = Math.exp(-t2 * 4.5) * (1 - Math.exp(-t2 * 90));
      val = (Math.sin(2 * Math.PI * nota * t2) * 0.5 + Math.sin(2 * Math.PI * nota * 2 * t2) * 0.18) * env * 0.85;
    }
    samples[i] = Math.max(-32768, Math.min(32767, Math.floor(val * 32767)));
  }
  return encodeWav(samples, sampleRate);
}

export function tocarBeep(): void {
  try {
    if (!chimeUri) chimeUri = gerarWav();
    const a = new Audio(chimeUri);
    a.volume = 1.0;
    a.play().catch(() => {});
  } catch {
    // ignora
  }
}

// Alerta de chamado novo: toca 2x (mais insistente que o beep de mensagem).
export function tocarAlertaChamado(): void {
  try {
    if (!alertaUri) alertaUri = gerarAlertaWav();
    const a = new Audio(alertaUri);
    a.volume = 1.0;
    a.play().then(() => {
      setTimeout(() => { try { new Audio(alertaUri!).play().catch(() => {}); } catch { /* noop */ } }, 700);
    }).catch(() => {});
  } catch {
    // ignora
  }
}

// Chamar uma vez no first-click do usuario pra "destravar" autoplay no Chrome/Safari.
export function destravarAutoplay(): void {
  if (unlocked) return;
  if (!chimeUri) chimeUri = gerarWav();
  if (!alertaUri) alertaUri = gerarAlertaWav();
  const a = new Audio(chimeUri);
  a.volume = 0;
  a.play().then(() => { a.pause(); unlocked = true; }).catch(() => {});
}
